import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Store } from 'lucide-react';
import LoretoGlyph from './LoretoGlyph';
import { useAuth } from '../context/AuthContext';
import { setPostLoginRedirect } from '../lib/pendingBuild';

/**
 * Mid-page conversion band, in the composition of the social card.
 *
 * The page had one place to act — the hero composer — and then nothing until
 * the footer CTA, which is about buying and selling rather than building. A
 * reader who scrolls through the argument and is convinced by it had no way to
 * say so. This catches them just after the marketplace section, which is the
 * top of intent: they have read what a skill is, what it costs, and how it
 * gets to their agent.
 *
 * It is built rather than dropped in as the PNG. Type in an image does not
 * scale on a retina screen, cannot be read by a crawler or a screen reader,
 * reflows nowhere on a phone, and cannot hold a button.
 */
export default function StartBuilding() {
  const navigate = useNavigate();
  const { isAuthed } = useAuth();

  // Same contract the hero composer uses: an unauthed click stashes where it
  // wanted to go, so signing in lands in the generator rather than dropping
  // the user on the dashboard having forgotten why they came.
  const startBuilding = () => {
    if (!isAuthed) {
      setPostLoginRedirect('/dashboard/generate');
      navigate('/dashboard');
      return;
    }
    navigate('/dashboard/generate');
  };

  return (
    <section
      className="relative overflow-hidden border-t border-[var(--border)] bg-[var(--bg)]"
      aria-label="Start building a skill"
    >
      {/* warm accent glow, top right — the same ambient wash as the hero */}
      <div
        className="pointer-events-none absolute top-[-40%] right-[-10%] w-[820px] h-[820px] rounded-full opacity-[0.13] blur-none md:blur-[150px]"
        style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 60%)' }}
        aria-hidden="true"
      />

      {/* The mark, oversized and cropped by the right edge — the same device as
          the social card. It has to actually bleed: a whole letter floating in
          the corner reads as a stray panel, not as a watermark. The glyph sits
          inside its own square box with padding, so the offset has to clear
          that as well as the frame. Hidden below md, where it would land under
          the copy rather than beside it. */}
      <div
        className="pointer-events-none hidden md:block absolute top-1/2 right-[-11%] lg:right-[-7%] -translate-y-1/2 opacity-[0.06]"
        aria-hidden="true"
      >
        <LoretoGlyph size={700} rule="transparent" glyph="var(--text)" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 md:py-28">
        <div>
          <div className="inline-flex items-center gap-2.5 mb-7 text-[var(--text-3)]">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] shrink-0" />
            <span className="mono text-[0.7rem] tracking-[0.18em] uppercase">
              The model is fixed — the harness is yours
            </span>
          </div>

          <h2 className="serif text-[2.8rem] md:text-[4.2rem] leading-[1.02] tracking-[-0.03em] max-w-[20ch]">
            Two minutes to{' '}
            <span className="italic text-[var(--text-2)]">your first piece.</span>
          </h2>

          <div className="w-[168px] h-[3px] bg-[var(--rust)] mt-8 mb-8" />

          <p className="text-[var(--text-2)] text-[1.02rem] leading-[1.7] mb-10 max-w-[54ch]">
            Paste a link to something you already know — a video, an article, your own
            notes — and get back a package that drops straight into
            <span className="mono text-[var(--accent)]"> .claude/skills/</span>. About two
            minutes, start to finish. When you want the parts nobody wrote down, point Loreto
            at the repository instead.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={startBuilding} className="btn-primary !py-3.5 !px-7 !text-[1rem]">
              Build your first skill <ArrowRight size={17} />
            </button>
            <Link to="/marketplace" className="btn-ghost !py-3.5 !px-7 !text-[1rem]">
              <Store size={17} /> Browse the marketplace
            </Link>
          </div>

          <div className="mt-7 mono text-[0.68rem] tracking-[0.16em] uppercase text-[var(--text-3)]">
            Free to start — two skills a month, no card
          </div>
        </div>
      </div>
    </section>
  );
}
